import ExcelJS from "exceljs";
import { format } from "date-fns";
import type { EsgBranchWasteExportResponse } from "../types/esg-dashboard.type";

const FILL_BRANCH_SUMMARY = "FFD9EAD3";
const FILL_MONTHLY_DETAIL = "FFD9EAF7";
const FILL_WASTE_TYPE = "FFFCE5CD";
const NUMBER_FORMAT = "#,##0.00";

function styleHeaderRow(row: ExcelJS.Row, fillColor: string): void {
  row.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
  });
}

function applyNumberFormat(sheet: ExcelJS.Worksheet, columns: string[]): void {
  columns.forEach((col) => {
    sheet.getColumn(col).numFmt = NUMBER_FORMAT;
  });
}

/**
 * Builds the branch waste breakdown workbook: Branch Summary (lifetime per branch),
 * Monthly Branch Detail (per branch per month), Waste Type Summary (lifetime per type).
 */
export async function buildBranchWasteExportWorkbook(
  data: EsgBranchWasteExportResponse,
): Promise<ExcelJS.Buffer> {
  const workbook = new ExcelJS.Workbook();

  const branchSummarySheet = workbook.addWorksheet("Branch Summary");
  branchSummarySheet.columns = [
    { header: "Branch", key: "branch", width: 37 },
    { header: "Total Waste (kg)", key: "total_waste_kg", width: 18 },
    { header: "Total Recycled (kg)", key: "total_recycled_kg", width: 21 },
    { header: "Diversion Rate (%)", key: "diversion_rate_percent", width: 20 },
  ];
  data.branch_summary.forEach((row) => branchSummarySheet.addRow(row));
  styleHeaderRow(branchSummarySheet.getRow(1), FILL_BRANCH_SUMMARY);
  branchSummarySheet.views = [{ state: "frozen", ySplit: 1 }];
  applyNumberFormat(branchSummarySheet, ["B", "C", "D"]);

  const monthlyDetailSheet = workbook.addWorksheet("Monthly Branch Detail");
  monthlyDetailSheet.columns = [
    { header: "Year", key: "year", width: 12 },
    { header: "Month", key: "month", width: 12 },
    { header: "Branch", key: "branch", width: 37 },
    { header: "Waste Diverted (kg)", key: "waste_diverted_kg", width: 21 },
    { header: "Waste Recycled (kg)", key: "waste_recycled_kg", width: 21 },
    { header: "Diversion Rate (%)", key: "diversion_rate_percent", width: 20 },
    { header: "Waste Breakdown", key: "waste_breakdown", width: 19 },
  ];
  data.monthly_detail.forEach((row) => monthlyDetailSheet.addRow(row));
  styleHeaderRow(monthlyDetailSheet.getRow(1), FILL_MONTHLY_DETAIL);
  monthlyDetailSheet.views = [{ state: "frozen", ySplit: 1 }];
  applyNumberFormat(monthlyDetailSheet, ["D", "E", "F"]);

  const wasteTypeSheet = workbook.addWorksheet("Waste Type Summary");
  wasteTypeSheet.columns = [
    { header: "Waste Type", key: "waste_type", width: 12 },
    { header: "Total Waste (kg)", key: "total_waste_kg", width: 18 },
  ];
  data.waste_type_summary.forEach((row) => wasteTypeSheet.addRow(row));
  styleHeaderRow(wasteTypeSheet.getRow(1), FILL_WASTE_TYPE);
  wasteTypeSheet.views = [{ state: "frozen", ySplit: 1 }];
  applyNumberFormat(wasteTypeSheet, ["B"]);

  return workbook.xlsx.writeBuffer();
}

export function downloadXlsxBuffer(buffer: ExcelJS.Buffer, filename: string): void {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildBranchWasteExportFilename(organizationName: string): string {
  const safeName = organizationName.trim().replace(/[^a-zA-Z0-9]+/g, "_") || "Organization";
  return `RECYCLEE_Branch_Waste_Breakdown_${safeName}_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
}
